import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import moment from 'moment-timezone';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import SearchButton from '../../components/atoms/SearchButton';
import CsvDownloadButton from '../../components/atoms/CsvDownloadButton';
import UserTable from '../organisms/UserTable';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { isAuthenticated, sendLogin } from '../../auth/Auth';
import { getUsers, downloadUser } from '../../actions/Users';

const styles = {
  root: {
    backgroundColor: '#e0e0e0',
    flexGrow: 1,
    width: '100%'
  },
  pageTitileDiv: {
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  mainPaper: {
    position: 'relative',
    margin: 20
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  mainPaperTitleDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 26px 10px 26px'
  },
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  buttonLeft: {
    textAlign: 'left',
    float: 'left'
  },
  buttonRight: {
    textAlign: 'right'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      name: state.loginInfo.name,
      statuses: state.users.statuses
    }),
    dispatch => ({
      actions: bindActionCreators({ getUsers, downloadUser }, dispatch)
    })
  ),
  setPropTypes(propTypes),
  withState('openModal', 'updateOpenModal', false),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openTable', 'updateOpenTable', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('accountId', 'setAccountId', ''),
  withState('displayName', 'setDisplayName', ''),
  withState('email', 'setEmail', ''),
  withState('status', 'setStatus', ''),
  withState('message', 'setMessage', ''),
  withState('page', 'setPage', 1),
  withState('rowsPerPage', 'setRowsPerPage', 100),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleChangePage: props => (event, page, limit) => {
      const table = document.getElementById('scrollTableId');
      if (table !== null) {
        table.scrollTop = 0;
      }
      props.setPage(page);

      const rowsPerPage = document.getElementById('rowsPerPage');
      let limitNum = 100;
      if (rowsPerPage) {
        limitNum = rowsPerPage.textContent;
      }

      const searchKey = {
        accountId: props.accountId,
        displayName: props.displayName,
        email: props.email,
        status: props.status,
        message: props.message,
        page,
        limit: limit || limitNum
      };

      props.updateLoading(true);
      props.updateOpenProgress(true);
      props.actions
        .getUsers(searchKey)
        .then(data => {
          if (data.error === true && data.payload.status === 403) {
            sendLogin(props);
          }
          props.updateOpenProgress(false);
          props.setResultMessage('');
          props.updateLoading(false);
          props.updateOpenTable(true);
        })
        .catch(() => {
          props.updateOpenProgress(false);
          props.setResultMessage('');
          props.updateLoading(false);
        });
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    handleChangePage,
    account,
    name,
    statuses,
    openDrawer,
    updateOpenDrawer,
    openTable,
    updateOpenTable,
    loading,
    updateLoading,
    openProgress,
    updateOpenProgress,
    resultMessage,
    setResultMessage,
    accountId,
    setAccountId,
    displayName,
    setDisplayName,
    email,
    setEmail,
    status,
    setStatus,
    message,
    setMessage,
    page
  }) => {
    const progress = {
      loading,
      updateLoading,
      openProgress,
      updateOpenProgress
    };

    const searchKey = {
      accountId,
      displayName,
      email,
      status,
      message
    };

    const searchAction = async () => {
      updateOpenTable(true);
      const rowsPerPage = document.getElementById('rowsPerPage');
      let limitNum = 100;
      if (rowsPerPage) {
        limitNum = rowsPerPage.textContent;
      }
      handleChangePage(null, 1, limitNum);

      // updateLoading(true);
      // updateOpenProgress(true);
      // await actions
      //   .getUsers(searchKey)
      //   .then(data => {
      //     checkSession(data);
      //     updateOpenProgress(false);
      //     setResultMessage('');
      //     updateLoading(false);
      //     handleChangePage(null, 0);
      //     updateOpenTable(true);
      //   })
      //   .catch(() => {
      //     updateOpenProgress(false);
      //     setResultMessage('');
      //     updateLoading(false);
      //   });
    };

    const downloadCsvBlob = (blob, fileName) => {
      if (window.navigator.msSaveOrOpenBlob) {
        // for IE,Edge
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        // for chrome, firefox
        const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
        const linkEl = document.createElement('a');
        linkEl.href = url;
        linkEl.setAttribute('download', fileName);
        document.body.appendChild(linkEl);
        linkEl.click();

        linkEl.parentNode.removeChild(linkEl);
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }
    };

    const csvDownloadAction = async () => {
      updateLoading(true);
      updateOpenProgress(true);
      const response = await actions
        .downloadUser(searchKey)
        .then(data => checkSession(data));
      if (response.type === 'DOWNLOAD_USER_SUCCESS') {
        const timeStamp = moment()
          .tz('Asia/Tokyo')
          .format('YYYYMMDDHHmm');
        updateOpenProgress(false);
        setResultMessage('');
        updateLoading(false);
        downloadCsvBlob(response.payload, `${timeStamp}_user_info.csv`);
      } else {
        setResultMessage('ユーザ情報ダウンロードに失敗しました。');
        updateLoading(false);
      }
    };

    const progressOkAction = () => {
      updateOpenProgress(false);
    };

    return (
      <div className={classes.root}>
        <Header
          name={name}
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            ユーザ検索
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <div>
              <TextField
                label="アカウントID"
                placeholder="部分一致"
                type="search"
                className={classes.textField}
                margin="normal"
                value={accountId}
                onChange={e => {
                  setAccountId(e.target.value);
                }}
              />
              <TextField
                label="名前"
                placeholder="部分一致"
                type="search"
                className={classes.textField}
                margin="normal"
                value={displayName}
                onChange={e => {
                  setDisplayName(e.target.value);
                }}
              />
            </div>
            <div>
              <TextField
                label="メールアドレス"
                placeholder="部分一致"
                type="search"
                className={classes.textField}
                margin="normal"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                }}
              />
              <TextField
                select
                label="ステータス"
                className={classes.textField}
                margin="normal"
                value={status}
                onChange={e => {
                  setStatus(e.target.value);
                }}
                InputLabelProps={{
                  shrink: true
                }}
              >
                {statuses.map(type => {
                  return (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  );
                })}
              </TextField>
            </div>
            <div>
              <TextField
                label="自己紹介メッセージ"
                placeholder="部分一致"
                type="search"
                className={classes.textField}
                margin="normal"
                value={message}
                onChange={e => {
                  setMessage(e.target.value);
                }}
              />
            </div>
            <div className={classes.buttonLeft}>
              <SearchButton onClickAction={searchAction} />
            </div>
            <div className={classes.buttonRight}>
              <CsvDownloadButton onClickAction={csvDownloadAction} />
            </div>
          </div>
          {openTable && (
            <UserTable
              progress={progress}
              page={page}
              handleChangePage={handleChangePage}
              selectedPage={page}
            />
          )}
        </Paper>
        <ProgressDialog
          openProgress={openProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
